/* eslint-disable @typescript-eslint/no-explicit-any */

import Grid from '@config/Grid/Grid'
import Tile from '@/shared/components/Tile/Tile'
import Table from '@/shared/components/Table/Table'
import { useTranslation } from 'react-i18next'
import {
  FILTER_KEYS,
  TABLE_KEYS
} from '@/shared/components/Table/models/Filter.model'
import styles from '@/features/employee-management/EmployeeManagement.module.scss'
import { getColumnsConfig } from '@/features/employee-management/EmployeeColumns.model'
import { useEmployeesQuery } from '@/features/employee-management/hooks/useEmployeesQuery'
import { useEmployeeFormConfig } from '@/features/employee-management/hooks/useEmployeeFormConfig'
import useEmployeeMetrics from '@/features/employee-management/hooks/useEmployeeMetrics'
import EmployeeRanking, {
  RankableEmployee
} from '@/features/employee-management/components/EmployeeRanking/EmployeeRanking'
import { useEffect, useMemo, useState } from 'react'
import DetailsModal from '@/shared/components/Modal/DetailsModal/DetailsModal'
import { useFormDirtyStore } from '@/shared/store/useFormDirtyStore'
import { useEmploymentProfiles } from '@/features/employee-management/hooks/useEmploymentProfiles'
import {
  ContractType,
  Position
} from '@/features/employee-management/types/employment.model'
import { formatPriceForInput, majorToMinor } from '@/shared/utils/price'
import { useImageSlots } from '@/shared/store/useImageSlots'
import { useEmployeeRankingStore } from '@/shared/store/useEmployeeRankingStore'
import IMAGE_SLOTS_KEYS from '@/shared/store/imageSlots.model'

function EmployeeManagement() {
  const { t } = useTranslation()
  const [employeeId, setEmployeeId] = useState<string>()
  const resetIsDirty = useFormDirtyStore(s => s.reset)
  const clearImageSlot = useImageSlots(s => s.clear)
  const isRankingOpen = useEmployeeRankingStore(s => s.isOpen)
  const setRankingOpen = useEmployeeRankingStore(s => s.setIsOpen)
  const resetRanking = useEmployeeRankingStore(s => s.reset)

  useEffect(() => resetIsDirty(), [])
  useEffect(() => () => resetRanking(), [resetRanking])

  // Open the ranking panel on entry so the user doesn't have to find the toggle.
  useEffect(() => {
    setRankingOpen(true)
  }, [setRankingOpen])

  const {
    employeesList,
    employeesListLoading,
    refetchEmployees,
    registerEmployeeAsync,
    updateEmployee,
    deleteEmployee,
    uploadAvatar
  } = useEmployeesQuery()

  const userIds = useMemo(
    () => (employeesList ?? []).map(e => e.userId),
    [employeesList]
  )
  const { metricsByUserId, isLoading: metricsLoading } =
    useEmployeeMetrics(userIds)

  const columnsModel = [
    'picture',
    'name',
    'role',
    'email',
    'serviceRating',
    'ordersServed',
    'hoursThisWeek',
    'status'
  ]

  const mappedEmployees = useMemo(
    () =>
      (employeesList ?? []).map(employee => {
        const metrics = metricsByUserId.get(employee.userId)
        return {
          id: employee.userId,
          picture: '',
          name: employee.name,
          email: employee.email,
          phone: employee.phoneNumber,
          role: metrics?.role ?? 'Waiter',
          status: metrics?.status ?? 'Active',
          serviceRating: metrics?.serviceRating ?? 0,
          ordersServed: metrics?.ordersServed ?? 0,
          hoursThisWeek: metrics?.hoursThisWeek ?? 0
        }
      }),
    [employeesList, metricsByUserId]
  )

  const rankingEmployees = useMemo<RankableEmployee[]>(
    () =>
      (employeesList ?? [])
        .map(employee => {
          const metrics = metricsByUserId.get(employee.userId)
          if (!metrics) return null
          return {
            id: employee.userId,
            name: employee.name,
            metrics
          }
        })
        .filter((e): e is RankableEmployee => e !== null),
    [employeesList, metricsByUserId]
  )

  const filtersList = [
    FILTER_KEYS.LANGUANGE_FILTER,
    FILTER_KEYS.CATEGORIES_FILTER
  ]

  const handleEditEmployee = (id: string) => setEmployeeId(id)
  const handleOnDeleteEmployee = (id: string) => deleteEmployee(id)

  const columnsConfig = getColumnsConfig(
    t,
    handleEditEmployee,
    handleOnDeleteEmployee
  )

  const initialFormConfig = useEmployeeFormConfig()
  const [formConfig, setFormConfig] = useState(initialFormConfig)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const defaultValues = useMemo(
    () => ({
      displayName: '',
      email: '',
      phoneNumber: '',
      position: null,
      contractType: null,
      hourlyRate: '',
      weeklyHoursLimit: ''
    }),
    []
  )

  const { profilesByUserId, saveProfile } = useEmploymentProfiles()

  /**
   * Employment data travels separately from identity: the user endpoint takes
   * name, e-mail and phone, the employment profile takes the rest.
   *
   * Every employment field is sent on every save, including the blank ones -
   * a field the user emptied has to clear the stored value, so it goes out as
   * an explicit `undefined` rather than being left off the object. Merging with
   * what is stored and dropping values the backend rejects is `saveProfile`'s
   * job.
   */
  const saveEmploymentProfile = (userId: string, data: any) =>
    saveProfile(userId, {
      position: (data.position?.value as Position) ?? undefined,
      contractType: (data.contractType?.value as ContractType) ?? undefined,
      hourlyRateMinorUnits: majorToMinor(data.hourlyRate),
      weeklyHoursLimit: data.weeklyHoursLimit
        ? Number(data.weeklyHoursLimit)
        : undefined
    })

  // Read at submit time rather than from a subscribed value: both submit
  // handlers are built before the user has cropped anything, the add one at
  // the click that opens the modal, the edit one inside an effect that does not
  // re-run on a crop, so a captured slot would always be the empty one.
  const uploadAvatarFromSlot = () => {
    const slot = useImageSlots.getState().slots[IMAGE_SLOTS_KEYS.EMPLOYEE_AVATAR]
    const blob = slot?.croppedBlob
    if (blob) {
      const file = new File([blob], 'avatar', {
        type: slot?.mime || 'image/png'
      })
      uploadAvatar(file)
    }
  }

  const handleAddEmployee = () => {
    const onSubmit = async (data: any) => {
      await registerEmployeeAsync({
        displayName: data.displayName,
        email: data.email,
        phoneNumber: data.phoneNumber
      })
      // Registration answers with an empty 201, and the employment profile is
      // keyed by user id, so the list is re-read and the new row found by the
      // e-mail just submitted. Without this the employment fields typed on the
      // add form are silently dropped.
      const { data: refreshed } = await refetchEmployees()
      const submittedEmail = String(data.email).trim().toLowerCase()
      const created = refreshed?.find(
        employee => employee.email.toLowerCase() === submittedEmail
      )
      if (created) saveEmploymentProfile(created.userId, data)
      uploadAvatarFromSlot()
      clearImageSlot(IMAGE_SLOTS_KEYS.EMPLOYEE_AVATAR)
      resetIsDirty()
      setIsModalOpen(false)
    }
    setFormConfig({ ...initialFormConfig, defaultValues, onSubmit })
    setIsModalOpen(true)
  }

  useEffect(() => {
    if (employeeId) {
      const employee = employeesList?.find(e => e.userId === employeeId)
      if (!employee) return
      const profile = profilesByUserId[employeeId]
      const editDefaultValues = {
        displayName: employee.name,
        email: employee.email,
        phoneNumber: employee.phoneNumber,
        position: profile?.position
          ? {
              label: t(
                `TABLE.EMPLOYEES.POSITIONS.${profile.position.toUpperCase()}`
              ),
              value: profile.position
            }
          : null,
        contractType: profile?.contractType
          ? {
              label: t(
                `TABLE.EMPLOYEES.CONTRACTS.${profile.contractType.toUpperCase()}`
              ),
              value: profile.contractType
            }
          : null,
        hourlyRate: formatPriceForInput(profile?.hourlyRateMinorUnits),
        weeklyHoursLimit: profile?.weeklyHoursLimit
          ? String(profile.weeklyHoursLimit)
          : ''
      }
      const onSubmit = (data: any) => {
        updateEmployee({
          // From the effect's own guard, not from page state read later, by
          // the time the request runs, `setEmployeeId(undefined)` below has
          // already happened.
          userId: employeeId,
          displayName: data.displayName,
          email: data.email,
          phoneNumber: data.phoneNumber
        })
        saveEmploymentProfile(employeeId, data)
        uploadAvatarFromSlot()
        clearImageSlot(IMAGE_SLOTS_KEYS.EMPLOYEE_AVATAR)
        resetIsDirty()
        setEmployeeId(undefined)
        setIsModalOpen(false)
      }
      setFormConfig({
        ...initialFormConfig,
        defaultValues: editDefaultValues,
        onSubmit
      })
      setIsModalOpen(true)
    }
    // profilesByUserId is a dependency because it arrives from the API a beat
    // after the employee list, and the modal would otherwise open with the
    // employment fields blank. Clearing employeeId on submit is what stops the
    // refetch that follows a save from reopening the modal on top of that.
  }, [employeeId, employeesList, profilesByUserId])

  const handleOnCloseModal = () => {
    clearImageSlot(IMAGE_SLOTS_KEYS.EMPLOYEE_AVATAR)
    setEmployeeId(undefined)
    setIsModalOpen(false)
  }

  const tableColClass = isRankingOpen ? 'start-2 col-7' : 'start-2 col-11'
  const rankingColClass = isRankingOpen ? 'start-9 col-4' : 'grid-hidden'

  return (
    <Grid>
      <div
        className={`${styles.employeeManagementTableContainer} ${tableColClass}`}
      >
        <Tile color="black">
          <div className={styles.tileInner}>
            <p className={`title-bold-medium ${styles.title}`}>
              {t('EMPLOYEES.TITLE')}
            </p>
            <p className={`body-medium ${styles.description}`}>
              {t('EMPLOYEES.DESCRIPTION')}
            </p>
            <div id="tour-employees-table" className={styles.tableWrapper}>
              <Table
                initialRows={mappedEmployees}
                columns={columnsConfig}
                columnsModel={columnsModel}
                filtersList={filtersList}
                tableKey={TABLE_KEYS.EMPLOYEES}
                filterModel={{ items: [] }}
                handleOnAddItem={handleAddEmployee}
                isLoading={employeesListLoading || metricsLoading}
              />
            </div>
          </div>
        </Tile>
      </div>
      <div className={`${styles.rankingContainer} ${rankingColClass}`}>
        <Tile color="black">
          <EmployeeRanking employees={rankingEmployees} />
        </Tile>
      </div>
      <DetailsModal
        title={
          employeeId
            ? t('TABLE.EMPLOYEES.EDIT_TITLE')
            : t('TABLE.EMPLOYEES.ADD_TITLE')
        }
        formConfig={formConfig}
        onClose={handleOnCloseModal}
        isOpen={isModalOpen}
      />
    </Grid>
  )
}

export default EmployeeManagement
